import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { authService } from "@data/services/authService"
import { inscriptionFunnelService } from "../../../../inscription/services/inscriptionFunnelService"

const initialState = {
    // Funnel state persisted server-side under this id (pre-auth lead capture).
    funnelStateId: null,
    userId: null,
    // Pré-rempli renvoyé par /init (HubSpot hydration) — firstname / email /
    // student_class. L'écran classe pioche dedans.
    initUser: null,
    // Bornes de la date de démarrage, servies par le backend. Le front ne les
    // calcule JAMAIS : la règle (J+48 h hors dimanche, zone morte de fin
    // d'année, fenêtre commerciale) vit dans InscriptionConfigService.
    startDateFloor: null,
    startDateCeiling: null,
    // Volumes hebdomadaires vendables PAR OFFRE ({essential: [1..5], …}). Un
    // créneau = une heure facturée, donc c'est aussi le plafond de la grille de
    // créneaux, et il dépend de l'offre choisie. Servi par le back, dérivé de
    // la grille tarifaire — jamais deviné ici.
    weeklyHoursByOffer: null,
    // Plafond de matières appliqué par la validation serveur. Servi, jamais
    // recopié : c'est leur divergence qui a produit un écran bloqué sans motif.
    maxSubjects: null,
    // Volume mis en avant sur le sélecteur d'heures (« le plus choisi »).
    recommendedWeeklyHours: null,
    // La seule formule vendue sans engagement ({ offer_type, weekly_hours }).
    // Absente, le funnel ne propose pas de couper l'engagement.
    noCommitment: null,
    // Le prix de chaque formule vendue, avec et sans engagement : les tuiles
    // de format y lisent leur tarif horaire.
    priceGrid: null,
    // Fuseaux servis par le back : la grille de créneaux énonce des heures de
    // PARIS (c'est l'offre, et c'est ce qui part au serveur) mais s'affiche à
    // l'heure de la famille. Servis plutôt que lus dans `staticData` /
    // `currentUser` comme sur le portail : ce funnel est ANONYME, il n'a ni
    // l'un ni l'autre avant l'inscription.
    serverTimezone: null,
    // Et anonyme, il n'a pas non plus de fuseau côté compte : `users.timezone`
    // s'écrit à la détection geoip du login, qu'un lead ne franchit pas.
    // `null` ici, PAS `deviceTimezone()` : un état persisté par un build
    // antérieur à cette clé écrase cette valeur par défaut au complet
    // (`autoMergeLevel2` ne fusionne pas plus profond que la tranche `data`),
    // donc l'amorcer ici ne protégeait pas l'appareil qui en a le plus besoin.
    // Le repli sur l'appareil vit désormais À LA LECTURE, dans
    // `resolveFunnelUserTimezone` — seule source de cette règle.
    userTimezone: null,
    // Où ce dossier choisit son format (`same_page` | `own_step`) : tiré par
    // le serveur à sa création, il ne change plus ensuite. `null` : pas dit —
    // le lecteur ({@see useInscriptionLead}) retombe sur la page de la formule.
    formatStep: null,
    // FAQ du checkout, servie par la config du funnel — même source que
    // l'écran d'abonnement (SubscriptionFaq côté backend).
    faq: [],
    // Catalogue matières (GET /subjects) et la classe pour laquelle il a été
    // chargé : partagé par les leads de l'onglet, il n'est servi qu'à cette
    // classe ({@see useInscriptionSubjects}). Pas persisté (`store.js`).
    subjects: null,
    subjectsClass: null,
}

// Où se range chaque clé de la config servie à l'entrée du funnel.
const CONFIG_KEYS = {
    startDateFloor: "start_date_floor",
    startDateCeiling: "start_date_ceiling",
    weeklyHoursByOffer: "weekly_hours_by_offer",
    maxSubjects: "max_subjects",
    recommendedWeeklyHours: "recommended_weekly_hours",
    noCommitment: "no_commitment",
    priceGrid: "price_grid",
    serverTimezone: "server_timezone",
    userTimezone: "user_timezone",
    faq: "faq",
}

/**
 * Une config servie est une PHOTO : chaque clé prend ce qu'elle dit, ou sa
 * valeur vide. Le serveur tait une clé pour dire non — `no_commitment` quand
 * il ne vend plus sans engagement, `user_timezone` pour un lead dont il ne
 * connaît pas le fuseau — et la tranche est persistée : garder la valeur
 * d'avant ferait proposer une formule qu'il refuse, ou annoncer à un lead
 * l'heure locale du précédent. Sans config dans la réponse, rien ne bouge.
 */
const withServedConfig = (state, config) =>
    config
        ? {
              ...state,
              ...Object.fromEntries(
                  Object.entries(CONFIG_KEYS).map(([key, served]) => [
                      key,
                      config[served] ?? initialState[key],
                  ])
              ),
          }
        : state

const withEntryPayload = (state, action) => {
    const payload = action.data || {}
    return {
        ...withServedConfig(state, payload.config),
        funnelStateId:
            payload.funnel_state_id ??
            payload.funnelStateId ??
            state.funnelStateId,
        userId: payload.user_id ?? payload.userId ?? state.userId,
        initUser: payload.user ?? state.initUser,
        formatStep: payload.format_step ?? null,
        // START/INIT ouvre un dossier NEUF : même sans config dans la réponse,
        // il n'hérite pas du fuseau du lead resté en mémoire (le commercial
        // qui enchaîne deux dossiers dans le même onglet). C'est le lecteur
        // (`mapStateToProps`, {@see resolveFunnelUserTimezone}) qui referme
        // sur l'appareil.
        userTimezone: payload.config?.user_timezone ?? null,
    }
}

export function inscriptionFunnel(state = initialState, action) {
    switch (action.type) {
        case inscriptionFunnelService.startInscriptionFunnel.name +
            apiConstants._SUCCESS:
        case inscriptionFunnelService.initInscriptionFromHubSpot.name +
            apiConstants._SUCCESS:
            return withEntryPayload(state, action)

        case inscriptionFunnelService.getInscriptionSubjects.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                subjects: action.data || [],
                subjectsClass: action.routeParams?.studentClass ?? null,
            }

        case inscriptionFunnelService.resumeInscription.name +
            apiConstants._SUCCESS: {
            const payload = action.data || {}
            return {
                ...withServedConfig(state, payload.config),
                funnelStateId: payload.funnel_state_id ?? state.funnelStateId,
                formatStep: payload.format_step ?? state.formatStep,
            }
        }

        // Reset on logout — required for any user-scoped data.
        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
            return initialState

        default:
            return state
    }
}

export default inscriptionFunnel
